import React, { useState, useEffect, useRef } from 'react';
import { Camera, Zap, Shield, Users, Calendar, MessageCircle, Heart, Share2, Bookmark, Send, ArrowRight, Play, Star, TrendingUp, ChevronRight, Instagram, Facebook, Linkedin } from 'lucide-react';
import phoneScreenImg from './phoneImageData.js';

const SwibeWebsite = () => {
  const [scrollY, setScrollY] = useState(0);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [showTerms, setShowTerms] = useState(false);
  const [showPrivacy, setShowPrivacy] = useState(false);
  const canvasRef = useRef(null);

  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    const handleMouseMove = (e) => {
      setMousePosition({
        x: (e.clientX / window.innerWidth - 0.5) * 20,
        y: (e.clientY / window.innerHeight - 0.5) * 20
      });
    };

    window.addEventListener('scroll', handleScroll);
    window.addEventListener('mousemove', handleMouseMove);

    // Advanced particle system
    const canvas = canvasRef.current;
    if (canvas) {
      const ctx = canvas.getContext('2d');
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;

      class Particle {
        constructor() {
          this.x = Math.random() * canvas.width;
          this.y = Math.random() * canvas.height;
          this.size = Math.random() * 3 + 1;
          this.speedX = Math.random() * 0.5 - 0.25;
          this.speedY = Math.random() * 0.5 - 0.25;
          this.opacity = Math.random() * 0.4 + 0.1;
          this.color = ['#9333ea', '#ec4899', '#06b6d4'][Math.floor(Math.random() * 3)];
        }

        update() {
          this.x += this.speedX;
          this.y += this.speedY;

          if (this.x > canvas.width) this.x = 0;
          if (this.x < 0) this.x = canvas.width;
          if (this.y > canvas.height) this.y = 0;
          if (this.y < 0) this.y = canvas.height;
        }

        draw() {
          ctx.fillStyle = this.color;
          ctx.globalAlpha = this.opacity;
          ctx.beginPath();
          ctx.arc(this.x, this.y, this.size, 0, Math.PI * 2);
          ctx.fill();
        }
      }

      const particles = Array.from({ length: 60 }, () => new Particle());

      const animate = () => {
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        particles.forEach(particle => {
          particle.update();
          particle.draw();
        });

        // Connect nearby particles
        particles.forEach((p1, i) => {
          particles.slice(i + 1).forEach(p2 => {
            const dx = p1.x - p2.x;
            const dy = p1.y - p2.y;
            const distance = Math.sqrt(dx * dx + dy * dy);

            if (distance < 150) {
              ctx.strokeStyle = `rgba(147, 51, 234, ${0.1 * (1 - distance / 150)})`;
              ctx.lineWidth = 1;
              ctx.globalAlpha = 0.2;
              ctx.beginPath();
              ctx.moveTo(p1.x, p1.y);
              ctx.lineTo(p2.x, p2.y);
              ctx.stroke();
            }
          });
        });

        requestAnimationFrame(animate);
      };

      animate();

      const handleResize = () => {
        canvas.width = window.innerWidth;
        canvas.height = window.innerHeight;
      };
      window.addEventListener('resize', handleResize);

      return () => window.removeEventListener('resize', handleResize);
    }

    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('mousemove', handleMouseMove);
    };
  }, []);

  const features = [
    {
      icon: <Calendar size={28} />,
      title: "Hangout Cards",
      description: "Make plans with new people and connect seamlessly. Discover local events and spontaneous meetups in your area."
    },
    {
      icon: <Camera size={28} />,
      title: "Immersive Stories",
      description: "Share moments with cinematic quality. Advanced editing tools and AR filters that bring stories to life."
    },
    {
      icon: <Shield size={28} />,
      title: "Privacy by Design",
      description: "End-to-end encryption for messages. Granular controls over who sees what. Your data stays yours."
    },
    {
      icon: <Users size={28} />,
      title: "Authentic Communities",
      description: "Build meaningful connections with people who share your passions. No algorithms promoting drama."
    },
    {
      icon: <Zap size={28} />,
      title: "Lightning Fast",
      description: "Optimized for speed without compromising quality. Instant loading, smooth scrolling, zero lag."
    },
    {
      icon: <MessageCircle size={28} />,
      title: "Rich Conversations",
      description: "Express yourself fully with voice notes, reactions, GIFs, and seamless video calls."
    }
  ];

  return (
    <div className="swibe-website">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Clash+Display:wght@400;500;600;700&family=Satoshi:wght@300;400;500;700;900&display=swap');

        * {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
        }

        :root {
          --purple-50: #faf5ff;
          --purple-100: #f3e8ff;
          --purple-200: #e9d5ff;
          --purple-600: #9333ea;
          --purple-700: #7e22ce;
          --pink-500: #ec4899;
          --cyan-500: #06b6d4;
          --orange-500: #f97316;
          --emerald-500: #10b981;
        }

        .swibe-website {
          font-family: 'Satoshi', sans-serif;
          background: #0a0a0a;
          color: #ffffff;
          overflow-x: hidden;
        }

        /* Particle Canvas */
        .particle-canvas {
          position: fixed;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          z-index: 0;
          pointer-events: none;
          opacity: 0.6;
        }

        /* Navigation - Modern Floating Pill */
        .navbar {
          position: fixed;
          top: 24px;
          left: 50%;
          transform: translateX(-50%);
          z-index: 1000;
          display: flex;
          align-items: center;
          gap: 2.5rem;
          padding: 1rem 2rem;
          background: rgba(10, 10, 10, 0.7);
          backdrop-filter: blur(20px) saturate(180%);
          border: 1px solid rgba(147, 51, 234, 0.2);
          border-radius: 100px;
          transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
          box-shadow: 0 8px 32px rgba(147, 51, 234, 0.2);
        }

        .navbar.scrolled {
          background: rgba(10, 10, 10, 0.95);
          box-shadow: 0 8px 48px rgba(147, 51, 234, 0.25);
        }

        .logo {
          font-family: 'Clash Display', sans-serif;
          font-size: 1.65rem;
          font-weight: 700;
          background: linear-gradient(135deg, var(--purple-600), var(--pink-500));
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
          letter-spacing: -0.02em;
        }

        .nav-links {
          display: flex;
          gap: 2.5rem;
          align-items: center;
        }

        .nav-links a {
          color: #e5e5e5;
          text-decoration: none;
          font-weight: 600;
          font-size: 0.95rem;
          transition: all 0.3s ease;
          position: relative;
        }

        .nav-links a:hover {
          color: var(--purple-600);
        }

        .nav-links a::after {
          content: '';
          position: absolute;
          bottom: -4px;
          left: 50%;
          transform: translateX(-50%);
          width: 4px;
          height: 4px;
          border-radius: 50%;
          background: var(--purple-600);
          opacity: 0;
          transition: opacity 0.3s ease;
        }

        .nav-links a:hover::after {
          opacity: 1;
        }

        .btn-nav {
          padding: 0.7rem 1.75rem;
          background: linear-gradient(135deg, var(--purple-600), var(--pink-500));
          color: white;
          border: none;
          border-radius: 50px;
          font-weight: 700;
          font-size: 0.9rem;
          cursor: pointer;
          transition: all 0.3s ease;
          box-shadow: 0 4px 16px rgba(147, 51, 234, 0.3);
        }

        .btn-nav:hover {
          transform: scale(1.05);
          box-shadow: 0 6px 24px rgba(147, 51, 234, 0.4);
        }

        /* Hero Section */
        .hero {
          min-height: 100vh;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 140px 5% 100px;
          position: relative;
          overflow: hidden;
          background: linear-gradient(180deg, #0a0a0a 0%, #1a0033 100%);
        }

        /* Modern Mesh Gradient Background */
        .mesh-gradient {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          background: 
            radial-gradient(at 27% 37%, rgba(147, 51, 234, 0.15) 0px, transparent 50%),
            radial-gradient(at 97% 21%, rgba(236, 72, 153, 0.12) 0px, transparent 50%),
            radial-gradient(at 52% 99%, rgba(6, 182, 212, 0.12) 0px, transparent 50%),
            radial-gradient(at 10% 29%, rgba(249, 115, 22, 0.1) 0px, transparent 50%),
            radial-gradient(at 97% 96%, rgba(16, 185, 129, 0.1) 0px, transparent 50%);
          filter: blur(80px);
          z-index: 0;
          animation: meshMove 20s ease-in-out infinite;
        }

        @keyframes meshMove {
          0%, 100% { transform: scale(1) rotate(0deg); }
          50% { transform: scale(1.05) rotate(2deg); }
        }

        /* 3D Floating Glass Cards */
        .floating-elements {
          position: absolute;
          width: 100%;
          height: 100%;
          z-index: 1;
          pointer-events: none;
        }

        .glass-morph {
          position: absolute;
          background: rgba(147, 51, 234, 0.1);
          backdrop-filter: blur(20px);
          border: 1px solid rgba(147, 51, 234, 0.3);
          border-radius: 32px;
          padding: 2.5rem;
          transform-style: preserve-3d;
          animation: float3d 10s ease-in-out infinite;
          box-shadow: 0 20px 60px rgba(147, 51, 234, 0.25);
        }

        .glass-1 {
          top: 12%;
          left: 5%;
          width: 220px;
          height: 220px;
          animation-delay: 0s;
        }

        .glass-2 {
          top: 55%;
          right: 8%;
          width: 200px;
          height: 200px;
          animation-delay: -5s;
        }

        .glass-3 {
          bottom: 18%;
          left: 12%;
          width: 180px;
          height: 180px;
          animation-delay: -2.5s;
        }

        @keyframes float3d {
          0%, 100% {
            transform: translateY(0) rotateX(8deg) rotateY(8deg) rotateZ(3deg);
          }
          50% {
            transform: translateY(-30px) rotateX(-8deg) rotateY(-8deg) rotateZ(-3deg);
          }
        }

        /* Gradient Orbs */
        .gradient-orb {
          position: absolute;
          border-radius: 50%;
          filter: blur(70px);
          opacity: 0.35;
          animation: orbFloat 12s ease-in-out infinite;
        }

        .orb-1 {
          width: 450px;
          height: 450px;
          background: radial-gradient(circle, var(--purple-600), transparent);
          top: 8%;
          right: 8%;
        }

        .orb-2 {
          width: 400px;
          height: 400px;
          background: radial-gradient(circle, var(--pink-500), transparent);
          bottom: 15%;
          left: 5%;
          animation-delay: -6s;
        }

        .orb-3 {
          width: 350px;
          height: 350px;
          background: radial-gradient(circle, var(--cyan-500), transparent);
          top: 45%;
          left: 35%;
          animation-delay: -3s;
        }

        @keyframes orbFloat {
          0%, 100% {
            transform: translate(0, 0) scale(1);
          }
          50% {
            transform: translate(40px, -40px) scale(1.15);
          }
        }

        .hero-content {
          max-width: 1400px;
          width: 100%;
          margin: 0 auto;
          display: grid;
          grid-template-columns: 1.1fr 0.9fr;
          gap: 6rem;
          align-items: center;
          position: relative;
          z-index: 2;
        }

        .hero-text {
          animation: fadeInUp 1s ease-out 0.3s backwards;
        }

        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .hero-text h1 {
          font-family: 'Clash Display', sans-serif;
          font-size: clamp(3.5rem, 7vw, 6.5rem);
          font-weight: 800;
          line-height: 1.05;
          margin-bottom: 2rem;
          letter-spacing: -0.04em;
          color: #ffffff;
        }

        .gradient-text {
          background: linear-gradient(135deg, var(--purple-600), var(--pink-500), var(--cyan-500));
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
          background-size: 200% 200%;
          animation: gradientShift 5s ease infinite;
        }

        @keyframes gradientShift {
          0%, 100% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
        }

        .hero-text p {
          font-size: 1.35rem;
          line-height: 1.75;
          color: #a3a3a3;
          margin-bottom: 3rem;
          font-weight: 400;
          max-width: 600px;
        }

        .hero-buttons {
          display: flex;
          gap: 1.25rem;
          flex-wrap: wrap;
          align-items: center;
        }

        .btn-primary {
          display: inline-flex;
          align-items: center;
          gap: 0.75rem;
          padding: 1.3rem 2.75rem;
          background: linear-gradient(135deg, var(--purple-600), var(--pink-500));
          color: white;
          border: none;
          border-radius: 16px;
          font-weight: 700;
          font-size: 1.05rem;
          cursor: pointer;
          transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
          box-shadow: 0 10px 40px rgba(147, 51, 234, 0.35);
          position: relative;
          overflow: hidden;
        }

        .btn-primary::before {
          content: '';
          position: absolute;
          top: 0;
          left: -100%;
          width: 100%;
          height: 100%;
          background: linear-gradient(135deg, var(--pink-500), var(--cyan-500));
          transition: left 0.5s cubic-bezier(0.4, 0, 0.2, 1);
        }

        .btn-primary:hover::before {
          left: 0;
        }

        .btn-primary span {
          position: relative;
          z-index: 1;
        }

        .btn-primary:hover {
          transform: translateY(-4px);
          box-shadow: 0 16px 50px rgba(147, 51, 234, 0.45);
        }

        .btn-secondary {
          display: inline-flex;
          align-items: center;
          gap: 0.75rem;
          padding: 1.3rem 2.75rem;
          background: rgba(147, 51, 234, 0.15);
          color: #ffffff;
          border: 2px solid rgba(147, 51, 234, 0.4);
          border-radius: 16px;
          font-weight: 700;
          font-size: 1.05rem;
          cursor: pointer;
          transition: all 0.3s ease;
        }

        .btn-secondary:hover {
          background: rgba(147, 51, 234, 0.3);
          border-color: rgba(147, 51, 234, 0.7);
          transform: translateY(-4px);
          box-shadow: 0 10px 30px rgba(147, 51, 234, 0.4);
        }

        /* Modern Bento Grid Phone Display */
        .hero-visual {
          position: relative;
          perspective: 2000px;
          animation: fadeInUp 1s ease-out 0.6s backwards;
        }

        .bento-container {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 1.25rem;
          transform-style: preserve-3d;
        }

        .bento-main {
          grid-column: 1 / -1;
          position: relative;
          transform-style: preserve-3d;
        }

        .phone-showcase {
          width: 100%;
          max-width: 400px;
          margin: 0 auto;
          transform-style: preserve-3d;
          animation: phone3dFloat 7s ease-in-out infinite;
        }

        @keyframes phone3dFloat {
          0%, 100% {
            transform: rotateY(-18deg) rotateX(6deg) translateZ(0);
          }
          50% {
            transform: rotateY(-18deg) rotateX(6deg) translateZ(35px);
          }
        }

        .phone-device {
          width: 100%;
          aspect-ratio: 9 / 19.5;
          background: linear-gradient(135deg, #1a1a1a, #0a0a0a);
          border-radius: 55px;
          padding: 16px;
          box-shadow: 
            0 60px 120px rgba(0, 0, 0, 0.4),
            0 0 0 1px rgba(255, 255, 255, 0.1) inset,
            -35px 0 70px rgba(147, 51, 234, 0.35),
            35px 0 70px rgba(236, 72, 153, 0.3);
          position: relative;
          transform: translateZ(50px);
        }

        .phone-notch {
          position: absolute;
          top: 16px;
          left: 50%;
          transform: translateX(-50%);
          width: 150px;
          height: 32px;
          background: #000;
          border-radius: 0 0 22px 22px;
          z-index: 10;
        }

        .phone-screen {
          width: 100%;
          height: 100%;
          border-radius: 45px;
          overflow: hidden;
          position: relative;
          background-size: cover;
          background-position: center;
          background-repeat: no-repeat;
        }

        .screen-shine {
          position: absolute;
          top: -50%;
          left: -50%;
          width: 200%;
          height: 200%;
          background: linear-gradient(45deg, 
            transparent 30%, 
            rgba(255, 255, 255, 0.1) 50%, 
            transparent 70%);
          animation: shine 10s ease-in-out infinite;
        }

        @keyframes shine {
          0%, 100% { transform: translate(-100%, -100%) rotate(45deg); }
          50% { transform: translate(100%, 100%) rotate(45deg); }
        }

        .screen-image {
          width: 100%;
          height: 100%;
          object-fit: cover;
          position: relative;
          z-index: 1;
        }

        .screen-ui {
          height: 100%;
          padding: 56px 26px 26px;
          display: flex;
          flex-direction: column;
          position: relative;
          z-index: 1;
        }

        .status-bar {
          display: flex;
          justify-content: space-between;
          color: white;
          font-size: 0.75rem;
          font-weight: 600;
          margin-bottom: 36px;
          opacity: 0.9;
        }

        .app-branding {
          text-align: center;
          color: white;
          margin-bottom: 36px;
        }

        .app-branding h3 {
          font-family: 'Clash Display', sans-serif;
          font-size: 2.25rem;
          font-weight: 800;
          margin-bottom: 6px;
          letter-spacing: -0.02em;
        }

        .app-branding p {
          font-size: 0.95rem;
          opacity: 0.85;
          font-weight: 500;
        }

        .feed-posts {
          flex: 1;
          display: flex;
          flex-direction: column;
          gap: 18px;
          overflow: hidden;
        }

        .feed-card {
          background: rgba(30, 30, 30, 0.9);
          border-radius: 26px;
          padding: 22px;
          animation: cardEnter 0.7s cubic-bezier(0.4, 0, 0.2, 1) backwards;
          box-shadow: 0 8px 32px rgba(147, 51, 234, 0.15);
          border: 1px solid rgba(147, 51, 234, 0.2);
        }

        .feed-card:nth-child(1) { animation-delay: 1.1s; }
        .feed-card:nth-child(2) { animation-delay: 1.3s; }

        @keyframes cardEnter {
          from {
            opacity: 0;
            transform: translateY(30px) scale(0.95);
          }
          to {
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }

        .card-header {
          display: flex;
          align-items: center;
          gap: 12px;
          margin-bottom: 14px;
        }

        .user-avatar {
          width: 44px;
          height: 44px;
          border-radius: 50%;
          background: linear-gradient(135deg, var(--purple-600), var(--pink-500));
          box-shadow: 0 4px 12px rgba(147, 51, 234, 0.25);
        }

        .user-info h4 {
          font-size: 0.9rem;
          font-weight: 700;
          color: #ffffff;
          margin-bottom: 2px;
        }

        .user-info span {
          font-size: 0.75rem;
          color: #a3a3a3;
          font-weight: 500;
        }

        .card-text p {
          color: #e5e5e5;
          font-size: 0.88rem;
          line-height: 1.6;
          margin-bottom: 12px;
        }

        .card-media {
          width: 100%;
          height: 130px;
          background: linear-gradient(135deg, rgba(147, 51, 234, 0.3), rgba(236, 72, 153, 0.3), rgba(6, 182, 212, 0.3));
          border-radius: 18px;
          margin-bottom: 14px;
          position: relative;
          overflow: hidden;
        }

        .card-media::after {
          content: '';
          position: absolute;
          inset: 0;
          background: linear-gradient(135deg, transparent, rgba(255, 255, 255, 0.3));
        }

        .card-interactions {
          display: flex;
          gap: 18px;
          padding-top: 10px;
          border-top: 1px solid rgba(147, 51, 234, 0.2);
        }

        .interaction-btn {
          display: flex;
          align-items: center;
          gap: 5px;
          color: #a3a3a3;
          font-size: 0.8rem;
          font-weight: 700;
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .interaction-btn:hover {
          color: var(--purple-600);
          transform: scale(1.08);
        }

        /* Modern Bento Items */
        .bento-card {
          background: rgba(147, 51, 234, 0.1);
          backdrop-filter: blur(20px);
          border: 1px solid rgba(147, 51, 234, 0.3);
          border-radius: 24px;
          padding: 2rem;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          text-align: center;
          transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
          animation: fadeInUp 1s ease-out backwards;
        }

        .bento-card:nth-child(2) { animation-delay: 0.8s; }
        .bento-card:nth-child(3) { animation-delay: 1s; }

        .bento-card:hover {
          background: rgba(147, 51, 234, 0.2);
          border-color: rgba(147, 51, 234, 0.5);
          transform: translateY(-6px) scale(1.02);
          box-shadow: 0 15px 40px rgba(147, 51, 234, 0.3);
        }

        .bento-icon {
          width: 56px;
          height: 56px;
          border-radius: 14px;
          background: linear-gradient(135deg, var(--purple-600), var(--pink-500));
          display: flex;
          align-items: center;
          justify-content: center;
          margin-bottom: 1.25rem;
          color: white;
          box-shadow: 0 8px 24px rgba(147, 51, 234, 0.3);
        }

        .bento-card h4 {
          font-family: 'Clash Display', sans-serif;
          font-size: 1.05rem;
          font-weight: 700;
          margin-bottom: 0.5rem;
          color: #ffffff;
        }

        .bento-card p {
          font-size: 0.8rem;
          color: #a3a3a3;
          font-weight: 500;
        }

        /* Features Section */
        .features {
          padding: 140px 5% 120px;
          background: #0a0a0a;
          position: relative;
        }

        .section-header {
          text-align: center;
          max-width: 850px;
          margin: 0 auto 100px;
        }

        .section-badge {
          display: inline-flex;
          align-items: center;
          gap: 0.6rem;
          padding: 0.6rem 1.5rem;
          background: rgba(147, 51, 234, 0.2);
          border: 1px solid rgba(147, 51, 234, 0.4);
          border-radius: 50px;
          font-size: 0.85rem;
          font-weight: 800;
          color: #a8d5ff;
          margin-bottom: 1.75rem;
          text-transform: uppercase;
          letter-spacing: 1px;
        }

        .section-header h2 {
          font-family: 'Clash Display', sans-serif;
          font-size: clamp(2.75rem, 5.5vw, 4.5rem);
          font-weight: 800;
          margin-bottom: 1.75rem;
          letter-spacing: -0.03em;
          line-height: 1.1;
          color: #ffffff;
        }

        .section-header p {
          font-size: 1.3rem;
          color: #a3a3a3;
          line-height: 1.75;
          font-weight: 400;
        }

        .features-grid {
          max-width: 1350px;
          margin: 0 auto;
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 2.5rem;
        }

        .feature-card {
          background: rgba(147, 51, 234, 0.08);
          border: 1px solid rgba(147, 51, 234, 0.2);
          border-radius: 32px;
          padding: 3rem 2.75rem;
          transition: all 0.5s cubic-bezier(0.4, 0, 0.2, 1);
          position: relative;
          overflow: hidden;
        }

        .feature-card::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          height: 4px;
          background: linear-gradient(90deg, var(--purple-600), var(--pink-500), var(--cyan-500));
          transform: scaleX(0);
          transform-origin: left;
          transition: transform 0.5s cubic-bezier(0.4, 0, 0.2, 1);
        }

        .feature-card::after {
          content: '';
          position: absolute;
          top: 50%;
          left: 50%;
          width: 0;
          height: 0;
          border-radius: 50%;
          background: radial-gradient(circle, rgba(147, 51, 234, 0.05), transparent);
          transform: translate(-50%, -50%);
          transition: width 0.6s ease, height 0.6s ease;
        }

        .feature-card:hover::before {
          transform: scaleX(1);
        }

        .feature-card:hover::after {
          width: 500px;
          height: 500px;
        }

        .feature-card:hover {
          transform: translateY(-12px);
          box-shadow: 0 32px 64px rgba(147, 51, 234, 0.35);
          border-color: rgba(147, 51, 234, 0.5);
        }

        .feature-icon {
          width: 72px;
          height: 72px;
          border-radius: 20px;
          background: linear-gradient(135deg, var(--purple-600), var(--pink-500));
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          margin-bottom: 2rem;
          transition: all 0.5s cubic-bezier(0.4, 0, 0.2, 1);
          position: relative;
          z-index: 1;
          box-shadow: 0 10px 30px rgba(147, 51, 234, 0.3);
        }

        .feature-card:hover .feature-icon {
          transform: scale(1.15) rotate(-6deg);
          box-shadow: 0 16px 45px rgba(147, 51, 234, 0.45);
        }

        .feature-card h3 {
          font-family: 'Clash Display', sans-serif;
          font-size: 1.7rem;
          font-weight: 700;
          margin-bottom: 1.25rem;
          color: #ffffff;
          position: relative;
          z-index: 1;
          letter-spacing: -0.02em;
        }

        .feature-card p {
          color: #a3a3a3;
          line-height: 1.8;
          font-size: 1.05rem;
          position: relative;
          z-index: 1;
        }

        /* CTA Section - Modern Glass Card */
        .cta-section {
          padding: 140px 5%;
          background: linear-gradient(180deg, #0a0a0a 0%, #1a0033 100%);
          position: relative;
        }

        .cta-wrapper {
          max-width: 1200px;
          margin: 0 auto;
          background: rgba(147, 51, 234, 0.08);
          backdrop-filter: blur(30px);
          border: 1px solid rgba(147, 51, 234, 0.25);
          border-radius: 48px;
          padding: 5.5rem 4.5rem;
          text-align: center;
          position: relative;
          overflow: hidden;
          box-shadow: 0 30px 80px rgba(147, 51, 234, 0.2);
        }

        .cta-wrapper::before {
          content: '';
          position: absolute;
          top: -50%;
          left: -50%;
          width: 200%;
          height: 200%;
          background: conic-gradient(from 0deg, transparent, rgba(147, 51, 234, 0.08), transparent);
          animation: rotate 12s linear infinite;
        }

        @keyframes rotate {
          100% { transform: rotate(360deg); }
        }

        .cta-content {
          position: relative;
          z-index: 1;
        }

        .cta-content h2 {
          font-family: 'Clash Display', sans-serif;
          font-size: clamp(3rem, 6vw, 5rem);
          font-weight: 800;
          margin-bottom: 2rem;
          letter-spacing: -0.03em;
          line-height: 1.1;
          color: #ffffff;
        }

        .cta-content p {
          font-size: 1.4rem;
          color: #a3a3a3;
          margin-bottom: 3.5rem;
          max-width: 750px;
          margin-left: auto;
          margin-right: auto;
          line-height: 1.75;
        }

        .download-grid {
          display: flex;
          gap: 1.75rem;
          justify-content: center;
          flex-wrap: wrap;
        }

        .download-btn {
          background: rgba(147, 51, 234, 0.15);
          border: 1px solid rgba(147, 51, 234, 0.3);
          border-radius: 24px;
          padding: 2.25rem 3.5rem;
          min-width: 280px;
          cursor: pointer;
          transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
        }

        .download-btn:hover {
          background: rgba(147, 51, 234, 0.3);
          border-color: rgba(147, 51, 234, 0.6);
          transform: translateY(-8px);
          box-shadow: 0 24px 56px rgba(147, 51, 234, 0.4);
        }

        .download-btn h3 {
          font-family: 'Clash Display', sans-serif;
          font-size: 1.75rem;
          font-weight: 700;
          margin-bottom: 0.75rem;
          color: #ffffff;
        }

        .download-btn p {
          font-size: 1rem;
          color: #a3a3a3;
          margin: 0;
          font-weight: 500;
        }

        /* Footer */
        .footer {
          background: #0a0a0a;
          color: white;
          padding: 100px 5% 50px;
        }

        .footer-grid {
          max-width: 1400px;
          margin: 0 auto;
          display: grid;
          grid-template-columns: 2fr 1fr 1fr 1fr;
          gap: 5rem;
          margin-bottom: 4rem;
        }

        .footer-brand h3 {
          font-family: 'Clash Display', sans-serif;
          font-size: 2rem;
          font-weight: 800;
          margin-bottom: 1.5rem;
          background: linear-gradient(135deg, #ffffff, #a855f7);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }

        .footer-brand p {
          color: #a3a3a3;
          line-height: 1.8;
          font-size: 1rem;
          margin-bottom: 2.5rem;
        }

        .social-links {
          display: flex;
          gap: 1rem;
        }

        .social-link {
          width: 50px;
          height: 50px;
          border-radius: 14px;
          background: rgba(255, 255, 255, 0.05);
          border: 1px solid rgba(255, 255, 255, 0.1);
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          cursor: pointer;
          transition: all 0.3s ease;
        }

        .social-link:hover {
          background: linear-gradient(135deg, var(--purple-600), var(--pink-500));
          border-color: transparent;
          transform: translateY(-4px);
          box-shadow: 0 10px 30px rgba(147, 51, 234, 0.4);
        }

        .footer-section h4 {
          font-family: 'Clash Display', sans-serif;
          font-size: 1.1rem;
          font-weight: 700;
          margin-bottom: 2rem;
        }

        .footer-section a {
          display: block;
          color: #a3a3a3;
          text-decoration: none;
          margin-bottom: 1.25rem;
          font-size: 0.95rem;
          transition: all 0.3s ease;
        }

        .footer-section a:hover {
          color: #a855f7;
          transform: translateX(4px);
        }

        .footer-bottom {
          max-width: 1400px;
          margin: 0 auto;
          padding-top: 3rem;
          border-top: 1px solid rgba(255, 255, 255, 0.08);
          display: flex;
          justify-content: space-between;
          align-items: center;
          color: #737373;
          font-size: 0.95rem;
        }

        .footer-links {
          display: flex;
          gap: 2.5rem;
        }

        .footer-links a {
          color: #737373;
          text-decoration: none;
          transition: color 0.3s ease;
        }

        .footer-links a:hover {
          color: #a855f7;
        }

        /* Legal Sections - Terms & Privacy */
        .legal-section {
          padding: 140px 5% 100px;
          background: #0a0a0a;
          min-height: 100vh;
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          z-index: 9999;
          overflow-y: auto;
        }

        .main-content.hidden {
          display: none;
        }

        .back-to-home {
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
          padding: 0.75rem 1.5rem;
          background: rgba(147, 51, 234, 0.15);
          border: 1px solid rgba(147, 51, 234, 0.3);
          border-radius: 12px;
          color: #ffffff;
          text-decoration: none;
          font-weight: 600;
          font-size: 0.95rem;
          margin-bottom: 2rem;
          transition: all 0.3s ease;
          cursor: pointer;
        }

        .back-to-home:hover {
          background: rgba(147, 51, 234, 0.3);
          border-color: rgba(147, 51, 234, 0.5);
          transform: translateX(-4px);
        }

        .legal-container {
          max-width: 900px;
          margin: 0 auto;
          background: rgba(147, 51, 234, 0.05);
          border: 1px solid rgba(147, 51, 234, 0.2);
          border-radius: 32px;
          padding: 4rem;
          backdrop-filter: blur(20px);
        }

        .legal-content h2 {
          font-family: 'Clash Display', sans-serif;
          font-size: 2.5rem;
          font-weight: 800;
          margin-bottom: 0.5rem;
          background: linear-gradient(135deg, var(--purple-600), var(--pink-500));
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
          text-align: center;
        }

        .last-updated {
          text-align: center;
          color: #a3a3a3;
          font-size: 0.95rem;
          margin-bottom: 3rem;
          font-weight: 500;
        }

        .legal-item {
          margin-bottom: 2.5rem;
          padding: 2rem;
          background: rgba(255, 255, 255, 0.02);
          border-radius: 16px;
          border: 1px solid rgba(147, 51, 234, 0.1);
        }

        .legal-item.warning {
          background: rgba(249, 115, 22, 0.05);
          border: 1px solid rgba(249, 115, 22, 0.3);
        }

        .legal-item h3 {
          font-family: 'Clash Display', sans-serif;
          font-size: 1.5rem;
          font-weight: 700;
          color: #ffffff;
          margin-bottom: 1rem;
        }

        .legal-item h4 {
          font-family: 'Satoshi', sans-serif;
          font-size: 1.2rem;
          font-weight: 700;
          color: #e5e5e5;
          margin-top: 1.5rem;
          margin-bottom: 0.75rem;
        }

        .legal-item p {
          color: #a3a3a3;
          line-height: 1.8;
          font-size: 1rem;
          margin-bottom: 1rem;
        }

        .legal-item ul {
          list-style-position: inside;
          margin-left: 1rem;
          color: #a3a3a3;
          line-height: 2;
        }

        .legal-item ul li {
          margin-bottom: 0.5rem;
          font-size: 1rem;
        }

        .legal-item strong {
          color: #e5e5e5;
          font-weight: 700;
        }

        .legal-item a {
          color: var(--purple-600);
          text-decoration: none;
          transition: color 0.3s ease;
        }

        .legal-item a:hover {
          color: var(--pink-500);
          text-decoration: underline;
        }

        /* Smooth scroll for anchor links */
        html {
          scroll-behavior: smooth;
        }

        /* Responsive */
        @media (max-width: 1200px) {
          .features-grid {
            grid-template-columns: repeat(2, 1fr);
          }

          .footer-grid {
            grid-template-columns: repeat(2, 1fr);
          }

          .footer-brand {
            grid-column: 1 / -1;
          }
        }

        @media (max-width: 968px) {
          .hero-content {
            grid-template-columns: 1fr;
            gap: 4rem;
          }

          .bento-container {
            grid-template-columns: 1fr;
          }

          .bento-main {
            grid-column: 1;
          }

          .features-grid {
            grid-template-columns: 1fr;
          }

          .footer-grid {
            grid-template-columns: 1fr;
          }

          .nav-links {
            display: none;
          }
        }

        @media (max-width: 640px) {
          .navbar {
            left: 20px;
            right: 20px;
            transform: none;
            padding: 0.75rem 1.25rem;
            gap: 1.25rem;
          }

          .btn-nav {
            padding: 0.6rem 1.25rem;
            font-size: 0.85rem;
          }

          .hero {
            padding: 120px 5% 80px;
          }

          .hero-text h1 {
            font-size: 2.5rem;
          }

          .hero-text p {
            font-size: 1.1rem;
          }

          .hero-buttons {
            flex-direction: column;
            width: 100%;
          }

          .btn-primary, .btn-secondary {
            width: 100%;
            justify-content: center;
          }

          .cta-wrapper {
            padding: 3.5rem 2.5rem;
          }

          .download-grid {
            flex-direction: column;
          }

          .download-btn {
            width: 100%;
          }

          .footer-bottom {
            flex-direction: column;
            gap: 2rem;
            text-align: center;
          }

          .footer-links {
            flex-direction: column;
            gap: 1rem;
          }

          .legal-container {
            padding: 2.5rem 1.5rem;
          }

          .legal-content h2 {
            font-size: 2rem;
          }

          .legal-item {
            padding: 1.5rem;
          }

          .legal-item h3 {
            font-size: 1.25rem;
          }

          .legal-item ul {
            margin-left: 0;
          }
        }
      `}</style>

      <canvas ref={canvasRef} className="particle-canvas" />

      {/* Main Content */}
      <div className={`main-content ${showTerms || showPrivacy ? 'hidden' : ''}`}>
        {/* Navigation */}
        <nav className={`navbar ${scrollY > 50 ? 'scrolled' : ''}`}>
          <div className="logo">swibe</div>
          <div className="nav-links">
            <a href="#home">Home</a>
            <a href="#features">Features</a>
            <a href="#download">Download</a>
          </div>
          <button className="btn-nav">Get Started</button>
        </nav>

        {/* Hero Section */}
        <section id="home" className="hero">
          <div className="mesh-gradient" />

          <div className="floating-elements">
            <div className="glass-morph glass-1" />
            <div className="glass-morph glass-2" />
            <div className="glass-morph glass-3" />
            <div className="gradient-orb orb-1" />
            <div className="gradient-orb orb-2" />
            <div className="gradient-orb orb-3" />
          </div>

          <div className="hero-content">
            <div className="hero-text">
              <h1>
                Where Moments<br />
                <span className="gradient-text">Come Alive</span>
              </h1>

              <p>
                Experience connection without compromise. Share authentic moments,
                discover meaningful content, and build communities that matter — all
                with privacy-first design and intelligent curation.
              </p>

              <div className="hero-buttons">
                <button className="btn-primary">
                  <span>Start Your Journey</span>
                  <ChevronRight size={22} />
                </button>
                <button className="btn-secondary">
                  <Play size={20} />
                  <span>Watch Demo</span>
                </button>
              </div>
            </div>

            <div className="hero-visual">
              <div className="bento-container">
                <div className="bento-main">
                  <div className="phone-showcase">
                    <div className="phone-device">
                      <div className="phone-screen" style={{ backgroundImage: `url(${phoneScreenImg})` }}>
                        <div className="screen-shine" />
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bento-card">
                  <div className="bento-icon">
                    <TrendingUp size={26} />
                  </div>
                  <h4>Trending Now</h4>
                  <p>See what's popular</p>
                </div>

                <div className="bento-card">
                  <div className="bento-icon">
                    <Star size={26} />
                  </div>
                  <h4>For You</h4>
                  <p>Personalized feed</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Features Section */}
        <section id="features" className="features">
          <div className="section-header">
            <div className="section-badge">
              <Calendar size={16} />
              <span>Features</span>
            </div>
            <h2>
              Everything You Need to <span className="gradient-text">Connect</span>
            </h2>
            <p>
              Powerful tools designed to help you share, discover, and build
              meaningful relationships in a beautiful, privacy-first environment.
            </p>
          </div>

          <div className="features-grid">
            {features.map((feature, index) => (
              <div key={index} className="feature-card">
                <div className="feature-icon">
                  {feature.icon}
                </div>
                <h3>{feature.title}</h3>
                <p>{feature.description}</p>
              </div>
            ))}
          </div>
        </section>

        {/* CTA Section */}
        <section id="download" className="cta-section">
          <div className="cta-wrapper">
            <div className="cta-content">
              <h2>
                Ready to Experience <span className="gradient-text">Swibe</span>?
              </h2>
              <p>
                Join a community that values authentic connection, creative expression,
                and your digital wellbeing. Download now and start your journey.
              </p>
              <div className="download-grid">
                <div className="download-btn" style={{ display: 'flex', alignItems: 'center', gap: '1.25rem', textAlign: 'left' }}>
                  <svg width="36" height="36" viewBox="0 0 24 24" fill="white">
                    <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z" />
                  </svg>
                  <div>
                    <p style={{ fontSize: '0.8rem', margin: 0, opacity: 0.7, fontWeight: 500 }}>Download on the</p>
                    <h3 style={{ fontSize: '1.5rem', margin: 0 }}>App Store</h3>
                  </div>
                </div>
                <div className="download-btn" style={{ display: 'flex', alignItems: 'center', gap: '1.25rem', textAlign: 'left' }}>
                  <svg width="36" height="36" viewBox="0 0 24 24" fill="white">
                    <path d="M3.18 23.73c-.41-.2-.68-.6-.68-1.05V1.32c0-.45.27-.85.68-1.05l11.1 11.73L3.18 23.73zm1.4.84L16.1 13.48l2.63 2.78L5.42 24.9c-.32.16-.63.04-.84-.33zm0-25.14c.21-.37.52-.49.84-.33l13.31 8.63-2.63 2.78L4.58-.57zM20.15 13.86l-3.15-3.33 3.15-3.33c.76.4 1.35.93 1.35 1.66v3.34c0 .73-.59 1.26-1.35 1.66z" />
                  </svg>
                  <div>
                    <p style={{ fontSize: '0.8rem', margin: 0, opacity: 0.7, fontWeight: 500 }}>Get it on</p>
                    <h3 style={{ fontSize: '1.5rem', margin: 0 }}>Google Play</h3>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Footer */}
        <footer className="footer">
          <div className="footer-grid">
            <div className="footer-brand">
              <h3>swibe</h3>
              <p>
                Reimagining social media with privacy, authenticity, and meaningful
                connections at the core. Join us in building a better digital future.
              </p>
              <div className="social-links">
                <div className="social-link">
                  <Instagram size={22} />
                </div>
                <div className="social-link">
                  <Facebook size={22} />
                </div>
                <div className="social-link">
                  <Linkedin size={22} />
                </div>
              </div>
            </div>

            <div className="footer-section">
              <h4>Product</h4>
              <a href="#">Features</a>
              <a href="#">Security</a>
              <a href="#">Updates</a>
              <a href="#">Roadmap</a>
            </div>

            <div className="footer-section">
              <h4>Company</h4>
              <a href="#">About</a>
              <a href="#">Careers</a>
              <a href="#">Press</a>
              <a href="#">Contact</a>
            </div>

            <div className="footer-section">
              <h4>Legal</h4>
              <a href="#" onClick={(e) => { e.preventDefault(); setShowTerms(true); }}>Terms & Conditions</a>
              <a href="#" onClick={(e) => { e.preventDefault(); setShowPrivacy(true); }}>Privacy Policy</a>
              <a href="#">Cookies</a>
              <a href="#">Guidelines</a>
            </div>
          </div>

          <div className="footer-bottom">
            <div>© 2024 Swibe Inc. All rights reserved.</div>
            <div className="footer-links">
              <a href="#" onClick={(e) => { e.preventDefault(); setShowPrivacy(true); }}>Privacy Policy</a>
              <a href="#" onClick={(e) => { e.preventDefault(); setShowTerms(true); }}>Terms</a>
              <a href="#">Sitemap</a>
            </div>
          </div>
        </footer>
      </div>

      {/* Terms and Conditions Modal/Section */}
      {showTerms && (
        <section id="terms" className="legal-section">
          <div className="legal-container">
            <button onClick={() => setShowTerms(false)} className="back-to-home">← Back to Home</button>
            <div className="legal-content">
              <h2>TERMS AND CONDITIONS</h2>
              <p className="last-updated">Last Updated: February 4, 2026</p>

              <div className="legal-item">
                <h3>1. Introduction</h3>
                <p>Welcome to Swibe. These Terms and Conditions ("Terms") govern your use of the Swibe mobile application (the "App") and any related services provided by Swibe App Development operating as Swibe ("we," "us," or "our").</p>
                <p>By downloading, accessing, or using Swibe, you agree to be bound by these Terms. If you do not agree to these Terms, please do not use the App.</p>
              </div>

              <div className="legal-item">
                <h3>2. Eligibility</h3>
                <p>You must be at least 18 years old to use Swibe. By using the App, you represent and warrant that you meet this age requirement and are legally capable of entering into a binding contract. If we discover anyone under 18 is using the service, we will terminate their account immediately.</p>
              </div>

              <div className="legal-item">
                <h3>3. User Accounts and Security</h3>
                <p>To use certain features of Swibe, you may need to register for an account. You agree to:</p>
                <ul>
                  <li>Provide accurate, current, and complete information.</li>
                  <li>Maintain the security of your login credentials.</li>
                  <li>Accept all responsibility for any activities that occur under your account.</li>
                  <li>Notify us immediately at support@swibeapp.com of any unauthorized use.</li>
                </ul>
              </div>

              <div className="legal-item">
                <h3>4. The Service and Location Data</h3>
                <p>Swibe is a social discovery platform designed to facilitate spontaneous, platonic hangouts based on user-generated "hangout cards."</p>
                <p><strong>Location Services:</strong> To function, the App requires access to your device's precise geographic location. By using Swibe, you consent to our collection and sharing of your approximate location data with other users in your vicinity. You can revoke location access in your device settings, but the core functionality of the App will cease to work.</p>
              </div>

              <div className="legal-item">
                <h3>5. User Content and Conduct</h3>
                <p>You are solely responsible for the content you post ("User Content"). You grant Swibe App Development a non-exclusive, royalty-free, worldwide license to use, display, and distribute your User Content on the App for the purpose of operating the service.</p>
                <p><strong>Prohibited Conduct:</strong> You agree NOT to post content that is illegal, harmful, sexually explicit, or promotes hate speech. We reserve the right to remove any User Content at our sole discretion.</p>
              </div>

              <div className="legal-item warning">
                <h3>6. Safety and Interactions (CRITICAL DISCLAIMER)</h3>
                <p>Swibe is a neutral platform. We do not conduct criminal background checks or identity verification screenings of our users.</p>
                <p>You are solely responsible for your interactions with other users. When meeting other users in person, you agree to take all necessary precautions:</p>
                <ul>
                  <li>Always meet in public, populated locations.</li>
                  <li>Inform a friend or family member of your plans.</li>
                  <li>Trust your instincts; leave if you feel uncomfortable.</li>
                </ul>
                <p><strong>RELEASE OF LIABILITY:</strong> IN NO EVENT SHALL SWIBE APP DEVELOPMENT, ITS AFFILIATES, OR AGENTS BE LIABLE FOR ANY DAMAGES WHATSOEVER, INCLUDING BODILY INJURY, EMOTIONAL DISTRESS, OR DEATH, ARISING OUT OF OR RELATING TO YOUR CONDUCT OR THE CONDUCT OF ANYONE ELSE IN CONNECTION WITH THE USE OF THE SERVICE.</p>
              </div>

              <div className="legal-item">
                <h3>7. Termination</h3>
                <p>We may terminate or suspend your access to our Service immediately, without prior notice, for any reason, including a breach of these Terms.</p>
              </div>

              <div className="legal-item">
                <h3>8. Disclaimers and Limitation of Liability</h3>
                <p>THE APP IS PROVIDED "AS-IS" WITHOUT WARRANTIES OF ANY KIND. TO THE MAXIMUM EXTENT PERMITTED BY LAW, SWIBE APP DEVELOPMENT SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, OR CONSEQUENTIAL DAMAGES RESULTING FROM YOUR USE OF THE APP.</p>
              </div>

              <div className="legal-item">
                <h3>9. Governing Law</h3>
                <p>These Terms shall be governed by the laws of Karnataka, India. You agree to submit to the jurisdiction of the courts located within Karnataka for the resolution of any disputes.</p>
              </div>

              <div className="legal-item">
                <h3>10. Changes to Terms</h3>
                <p>We reserve the right to modify these Terms at any time. Your continued use of the App after changes constitutes acceptance of those changes.</p>
              </div>

              <div className="legal-item">
                <h3>11. Contact Us</h3>
                <p>If you have any questions, contact us at: <a href="mailto:support@swibeapp.com">support@swibeapp.com</a></p>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Privacy Policy Section */}
      {showPrivacy && (
        <section id="privacy" className="legal-section">
          <div className="legal-container">
            <button onClick={() => setShowPrivacy(false)} className="back-to-home">← Back to Home</button>
            <div className="legal-content">
              <h2>PRIVACY POLICY</h2>
              <p className="last-updated">Last Updated: February 4, 2026</p>

              <div className="legal-item">
                <h3>1. Introduction</h3>
                <p>Swibe App Development ("we," "us," or "our") operates the Swibe mobile application (the "App"). This Privacy Policy informs you of our policies regarding the collection, use, and disclosure of personal data when you use our Service and the choices you have associated with that data.</p>
                <p>By using the App, you agree to the collection and use of information in accordance with this policy.</p>
              </div>

              <div className="legal-item">
                <h3>2. Information We Collect</h3>
                <p>We collect several types of information to provide and improve our social discovery service for you.</p>

                <h4>A. Information You Provide to Us</h4>
                <ul>
                  <li><strong>Registration Data:</strong> Your name, email address, date of birth, and gender provided during account creation.</li>
                  <li><strong>Profile Information:</strong> Bio details, interests, and profile photos you choose to upload.</li>
                  <li><strong>User Content:</strong> Content contained within "Hangout Cards" you post, including activity descriptions and meeting times.</li>
                </ul>

                <h4>B. Information We Collect Automatically</h4>
                <ul>
                  <li><strong>Precise Location Data:</strong> Swibe is a location-based service. We collect your device's precise geographic location (GPS) while the App is in use to show you nearby users and hangout opportunities.</li>
                  <li><strong>Usage Data:</strong> Details of how you interact with the App, including features used and time spent.</li>
                  <li><strong>Device Information:</strong> Your IP address, device type, operating system version, and unique device identifiers.</li>
                </ul>
              </div>

              <div className="legal-item">
                <h3>3. How We Use Your Information</h3>
                <p>We use the collected data for the following purposes:</p>
                <ul>
                  <li>To facilitate the core "social discovery" feature by showing your hangout cards to users within your immediate vicinity.</li>
                  <li>To notify you of nearby users who are looking to hang out.</li>
                  <li>To provide customer support and respond to your inquiries.</li>
                  <li>To detect, prevent, and address technical issues or fraudulent activity.</li>
                </ul>
              </div>

              <div className="legal-item">
                <h3>4. Sharing and Disclosure of Data</h3>
                <p><strong>Public Visibility:</strong> Any information you post on your profile or within a Hangout Card will be visible to other Swibe users. This includes your name, photo, and approximate distance from them.</p>
                <p><strong>Legal Requirements:</strong> We may disclose your personal data if required to do so by law or in response to valid requests by public authorities (e.g., a court or a government agency).</p>
                <p><strong>Service Providers:</strong> We use third-party tools (such as Google Firebase) to host the App and manage data. These providers only have access to your data to perform specific tasks on our behalf.</p>
              </div>

              <div className="legal-item">
                <h3>5. Data Security</h3>
                <p>The security of your data is a top priority. We use industry-standard encryption and security measures to protect your information. However, please be aware that no method of transmission over the internet is 100% secure, and we cannot guarantee absolute security.</p>
              </div>

              <div className="legal-item">
                <h3>6. Children's Privacy</h3>
                <p>Swibe is strictly for users aged 18 and older. We do not knowingly collect personal data from anyone under the age of 18. If we become aware that a minor has provided us with personal data, we will delete that information and terminate the account immediately.</p>
              </div>

              <div className="legal-item">
                <h3>7. Your Data Rights</h3>
                <p>You have the right to access, update, or delete the personal information we have on you. You can manage most of this directly within the App's settings. To request a full account deletion and removal of all data from our servers, please contact us at the email address provided below.</p>
              </div>

              <div className="legal-item">
                <h3>8. Governing Law</h3>
                <p>This Privacy Policy is governed by and construed in accordance with the laws of Karnataka, India, without regard to its conflict of law provisions.</p>
              </div>

              <div className="legal-item">
                <h3>9. Changes to This Privacy Policy</h3>
                <p>We may update our Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page and updating the "Last Updated" date at the top.</p>
              </div>

              <div className="legal-item">
                <h3>10. Contact Us</h3>
                <p>If you have any questions about this Privacy Policy or our data practices, please contact us:</p>
                <ul>
                  <li>Email: <a href="mailto:support@swibeapp.com">support@swibeapp.com</a></li>
                  <li>Website: <a href="https://www.swibeapp.com/privacy">www.swibeapp.com/privacy</a></li>
                </ul>
              </div>
            </div>
          </div>
        </section>
      )}
    </div>
  );
};

export default SwibeWebsite;